import { useEffect, useState } from "react";
import { FolderOpen, Gift, Plus, ClipboardList, PlayCircle, ShoppingBag, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { readCart } from "../lib/cart";
import { api, getCoachToken } from "../lib/api";

const GamesLink = () => {
  return (
    <a href="/fundraising-games" data-testid="header-games-link" aria-label="Fundraising games" className="flex items-center gap-1.5 px-2 py-2 text-sm font-semibold text-slate-800 hover:text-lime-700 sm:px-3">
      <Target className="h-4 w-4" /> <span className="lg:hidden">Games</span><span className="hidden lg:inline">Fundraiser games</span>
    </a>
  );
};

const CoachLink = () => {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const refresh = () => (getCoachToken() ? api.get("/coach/notifications").then((r) => setUnread(r.data.unread)).catch(() => setUnread(0)) : setUnread(0));
    refresh();
    window.addEventListener("ss-coach-updated", refresh);
    return () => window.removeEventListener("ss-coach-updated", refresh);
  }, []);
  return (
    <a href="/coach" data-testid="header-coach-link" aria-label="Coaches" className="relative flex items-center gap-1.5 px-2 py-2 sm:px-3 text-sm font-semibold text-slate-800 hover:text-lime-700">
      <ClipboardList className="h-4 w-4" /> <span className="hidden sm:inline">Coaches</span>
      {unread > 0 && <span className="absolute -right-0.5 top-0 bg-lime-400 px-1.5 text-[10px] font-black" data-testid="header-coach-unread">{unread}</span>}
    </a>
  );
};

export const Logo = () => (
  <a href="/" className="flex shrink-0 items-center" data-testid="logo-link" aria-label="sportsshields.ie home">
    <img src="/img/logo.png" alt="sportsshields.ie" className="hidden h-9 w-auto sm:block" />
    <img src="/img/logo-mark.png" alt="" aria-hidden="true" className="h-10 w-auto sm:hidden" />
  </a>
);

const CartLink = () => {
  const [n, setN] = useState(() => readCart().length);
  useEffect(() => {
    const f = () => setN(readCart().length);
    window.addEventListener("ss-cart-updated", f); window.addEventListener("storage", f);
    return () => { window.removeEventListener("ss-cart-updated", f); window.removeEventListener("storage", f); };
  }, []);
  if (!n) return null;
  return (
    <Link to="/order" aria-label={`Basket, ${n} shield${n === 1 ? "" : "s"}`} data-testid="header-cart-link" className="relative flex items-center gap-1.5 px-2 py-2 text-sm font-semibold text-slate-800 hover:text-lime-700 sm:px-3">
      <ShoppingBag className="h-4 w-4" /> <span className="hidden sm:inline">Basket</span>
      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-lime-500 px-1 text-[11px] font-bold text-slate-900" data-testid="header-cart-count">{n}</span>
    </Link>
  );
};

export const Header = ({ onDrafts, onNew }) => (
  <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-4 sm:px-6">
      <Logo />
      <nav className="flex items-center gap-1 sm:gap-2">
        <a href="/help" data-testid="header-help-link" aria-label="How-to videos" className="flex items-center gap-1.5 px-2 py-2 text-sm font-semibold text-slate-800 hover:text-lime-700 sm:px-3">
          <PlayCircle className="h-4 w-4" /> <span className="hidden sm:inline">How-to</span>
        </a>
        <a href="/gift" data-testid="header-gift-link" aria-label="Gift vouchers" className="flex items-center gap-1.5 px-2 py-2 text-sm font-semibold text-red-600 hover:text-red-700 sm:px-3">
          <Gift className="h-4 w-4" /> <span className="hidden md:inline">Gift vouchers</span>
        </a>
        <GamesLink />
        <CoachLink />
        <CartLink />
        <button type="button" onClick={onNew} data-testid="header-new-shield-button" aria-label="New shield"
          className="flex items-center gap-1.5 px-2 py-2 sm:px-3 text-sm font-semibold text-slate-800 transition-colors hover:text-lime-700">
          <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New shield</span>
        </button>
        {onDrafts && <button type="button" onClick={onDrafts} data-testid="header-my-drafts-button" aria-label="My drafts"
          className="flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1.5 text-sm font-bold sm:px-3 transition-colors hover:bg-slate-900 hover:text-white">
          <FolderOpen className="h-4 w-4" /> <span className="hidden sm:inline">My drafts</span>
        </button>}
      </nav>
    </div>
    <div className="stripe-rule" />
  </header>
);
